import {
  WSAlert,
  WSAlertAction,
  WSAlertProps,
  WSElement,
  WSFlexBox,
  WSList,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import {
  PayeeEngagementType,
  WorkLog,
  WorkLogStatus
} from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";
import { selectorWorkLogConvertedResourceLabels } from "../selectors/selectorWorkLogConvertedResourceLabels";
import { selectorWorkLogWorkerNoun } from "../selectors/selectorWorkLogWorkerNoun";
import { isStatusDiscarded } from "../utils/workLogPermissions";

type AlertConfig = Pick<WSAlertProps, "theme" | "icon" | "title"> & {
  message: React.ReactNode;
  action?: WSAlertAction;
};

type Props = {
  workLog: WorkLog | undefined;
  onRequestChanges?: () => void;
  onViewHistory?: () => void;
  /**
   * Opens the pay statement / invoice the work log converted into. Owned by the
   * route, which already resolves the right destination per resource type.
   */
  onViewConvertedResource?: () => void;
  contractorName?: string;
  /**
   * The work log can no longer reach a payroll run for its pay period — see
   * `selectorWorkLogApprovalExpired`. Replaces the "Action required" alert.
   */
  isApprovalExpired?: boolean;
  /** Days left on the payroll run's approval deadline, when there is one. */
  approvalDaysLeft?: number;
  /** Starts the off-cycle payroll run needed to pay an expired work log. */
  onCreateOffCycleRun?: () => void;
  /**
   * Decides whether an unapproved work log is described as becoming a pay
   * statement or an invoice — `convertedResourceType` is only set after
   * approval, so before that the engagement is the only signal.
   */
  engagementType?: PayeeEngagementType | null;
  /** The converted pay statement has been paid; nothing about it can change. */
  isConvertedResourcePaid?: boolean;
};

const InlineComment: React.FC<{
  label: string;
  comment: string;
  date?: Date | string;
}> = ({ label, comment, date }) => (
  <WSElement mt="M" colorBackground="white" p="M" style={{ borderRadius: 2 }}>
    <WSFlexBox.CenterY wrap="wrap" gap="S">
      <WSText.ParagraphXs color="gray500">{label}</WSText.ParagraphXs>
      {date && (
        <>
          <WSElement
            colorBackground="gray400"
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              flexShrink: 0
            }}
          />
          <WSText.ParagraphXs color="gray500">
            {toWSDateString(date, "monthDayYear")}
          </WSText.ParagraphXs>
        </>
      )}
    </WSFlexBox.CenterY>
    <WSText.ParagraphXs color="gray600" py="S">
      &ldquo;{comment}&rdquo;
    </WSText.ParagraphXs>
  </WSElement>
);

export const AlertWorkLogReviewStatusPayer: React.FC<Props> = ({
  workLog,
  onRequestChanges,
  onViewHistory,
  onViewConvertedResource,
  contractorName,
  isApprovalExpired,
  approvalDaysLeft,
  onCreateOffCycleRun,
  engagementType,
  isConvertedResourcePaid
}) => {
  const payerComment = workLog?.payerOwnedData?.comment ?? undefined;
  const payeeComment = workLog?.payeeOwnedData?.comment ?? undefined;

  // TODO(v3): comment timestamp now read from owned-data `commentAt` (v1 used the
  // work log's top-level `updatedAt`, which no longer exists).
  const commentDate =
    (payerComment
      ? workLog?.payerOwnedData?.commentAt
      : workLog?.payeeOwnedData?.commentAt) ?? undefined;

  // A W-2 work log converts into a pay statement, a 1099 one into an invoice —
  // and the person behind it is an "employee" rather than a "contractor".
  const convertedResourceLabels = selectorWorkLogConvertedResourceLabels({
    convertedResourceType: workLog?.convertedResourceType,
    engagementType
  });
  const convertedResourceNoun = convertedResourceLabels.noun;
  const workerNoun = selectorWorkLogWorkerNoun(engagementType);

  // Only one comment exists at a time
  const comment = payerComment || payeeComment;
  const commentLabel = payerComment
    ? "Your comment"
    : `Comment from ${contractorName || workerNoun}`;

  // Shown alongside every in-review alert, expired included — the payer's options
  // don't change with the reason the work log is sitting there.
  const aboutReviewsAlert = useMemo(
    (): AlertConfig => ({
      theme: "info",
      icon: "info-circle",
      title: "About work log reviews",
      message: (
        <>
          This work log was submitted for your review. You can:
          <br />
          {"• "}
          <WSText.ParagraphSm inline weight="medium">
            Approve
          </WSText.ParagraphSm>{" "}
          {`it as-is to generate a ${convertedResourceNoun}`}
          <br />
          {"• "}
          <WSText.ParagraphSm inline weight="medium">
            Edit
          </WSText.ParagraphSm>{" "}
          work summary below (add comments, edit quantities/amounts, or remove
          items)
          <br />
          {"• "}
          <WSText.ParagraphSm inline weight="medium">
            Return for editing,
          </WSText.ParagraphSm>{" "}
          {`send back to ${workerNoun} for changes`}
          <br />
          If the work log is not approved by the upcoming payment cycle, it will
          roll to the next cycle.
        </>
      )
    }),
    [convertedResourceNoun, workerNoun]
  );

  const alertConfigs = useMemo((): AlertConfig[] => {
    const configs: AlertConfig[] = [];

    switch (workLog?.status) {
      case WorkLogStatus.APPROVED:
        configs.push({
          theme: "success",
          icon: "check-circle",
          // Paid is the end of the line — the money has moved and nothing about
          // the work log or its statement can change any more, so the design
          // gives it its own headline rather than reusing "approved".
          title: isConvertedResourcePaid
            ? "Work log paid"
            : "Work log approved",
          message: (
            <>
              {`Work log was approved and a ${convertedResourceNoun} was generated.`}
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={commentDate}
                />
              )}
            </>
          ),
          action:
            workLog.convertedResourceId && onViewConvertedResource
              ? {
                  text: convertedResourceLabels.view,
                  onClick: onViewConvertedResource
                }
              : undefined
        });
        break;
      case WorkLogStatus.IN_REVIEW:
        // The approval window is gone: approving can no longer reach a run for
        // this pay period, so the only remaining route to payment is off-cycle.
        if (isApprovalExpired) {
          configs.push(
            {
              theme: "error",
              icon: "time",
              title: "Approval expired",
              message: (
                <>
                  {`The payroll run for this pay period has closed, so you can no longer approve this work log. Because it wasn't approved in time, it was not included in the payroll run and the ${workerNoun} wasn't paid for it. To pay this work log, create an off-cycle payroll run for the same pay period.`}
                  {comment && (
                    <InlineComment
                      label={commentLabel}
                      comment={comment}
                      date={commentDate}
                    />
                  )}
                </>
              ),
              action: onCreateOffCycleRun
                ? {
                    text: "Create an off-cycle run",
                    onClick: onCreateOffCycleRun
                  }
                : undefined
            },
            aboutReviewsAlert
          );
          break;
        }

        configs.push(
          {
            theme: "warning",
            icon: "alert",
            title: "Action required",
            message: (
              <>
                {payeeComment ? (
                  <>
                    {`The ${workerNoun} has requested editing for the submitted work log. To return it to the ${workerNoun} for changes, click `}
                    <WSText.ParagraphSm inline weight="medium">
                      &quot;Return for editing&quot;
                    </WSText.ParagraphSm>{" "}
                    or approve the work log as-is.{" "}
                  </>
                ) : (
                  "Awaiting your approval. "
                )}
                {approvalDaysLeft === undefined
                  ? "Approved work logs are included in the current payroll run. If you don't approve it in time, you'll need to pay it through an off-cycle payroll run instead."
                  : `Work logs approved within ${approvalDaysLeft} ${
                      approvalDaysLeft === 1 ? "day" : "days"
                    } will be included in the current payroll run. If you don't approve it in time, you'll need to pay it through an off-cycle payroll run instead.`}
                {comment && (
                  <InlineComment
                    label={commentLabel}
                    comment={comment}
                    date={commentDate}
                  />
                )}
              </>
            ),
            action:
              payeeComment && onRequestChanges
                ? {
                    text: "Return for editing",
                    onClick: onRequestChanges
                  }
                : undefined
          },
          aboutReviewsAlert
        );
        break;
      case WorkLogStatus.IN_PROGRESS:
        configs.push({
          theme: "info",
          icon: "time",
          title: `Awaiting ${workerNoun}`,
          message: (
            <>
              {`Work log sent back to ${workerNoun} to make changes. Awaiting resubmission from ${workerNoun}.`}
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={commentDate}
                />
              )}
            </>
          )
        });
        break;
      case WorkLogStatus.REJECTED:
        configs.push({
          theme: "error",
          icon: "block",
          title: "Rejected",
          message: (
            <>
              {`The work log was rejected and no ${convertedResourceNoun} was created.`}
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={commentDate}
                />
              )}
            </>
          ),
          action: onViewHistory
            ? {
                text: "View history",
                onClick: onViewHistory
              }
            : undefined
        });
        break;
    }

    // Handle "Discarded" status (not in the SDK WorkLogStatus enum yet).
    // Neutral, not an error: discarding an empty auto-submitted work log is the
    // expected outcome (hundreds a period is normal), not something that failed.
    if (isStatusDiscarded(workLog?.status as string)) {
      configs.push({
        theme: "neutral",
        icon: "info-circle",
        title: "Discarded",
        message:
          "This work log was empty and automatically submitted, so it was invalid and has been discarded. You can re-open this work log any time.",
        action: onViewHistory
          ? {
              text: "View history",
              onClick: onViewHistory
            }
          : undefined
      });
    }

    return configs;
  }, [
    workLog,
    comment,
    commentDate,
    commentLabel,
    payeeComment,
    convertedResourceNoun,
    convertedResourceLabels,
    workerNoun,
    aboutReviewsAlert,
    approvalDaysLeft,
    isApprovalExpired,
    isConvertedResourcePaid,
    onRequestChanges,
    onCreateOffCycleRun,
    onViewConvertedResource,
    onViewHistory
  ]);

  if (alertConfigs.length === 0) {
    return null;
  }

  return (
    <WSList gap="M">
      {alertConfigs.map(({ message, ...alertProps }, index) => (
        <WSAlert key={index} {...alertProps}>
          {message}
        </WSAlert>
      ))}
    </WSList>
  );
};
